import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    Tab,
    Tabs,
    Avatar,
    Chip,
    CircularProgress,
    Alert,
    IconButton,
    Tooltip,
    Divider,
    Paper,
    Button,
    TextField,
    FormControlLabel,
    Checkbox,
    Badge,
    Dialog,
    DialogContent,
    DialogActions
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    SportsSoccer as SportsSoccerIcon,
    AccessTime as AccessTimeIcon,
    EmojiEvents as EmojiEventsIcon,
    CropSquare as CropSquareIcon,
    Stop as StopIcon,
    Groups as GroupsIcon,
    Info as InfoIcon,
    Timeline as TimelineIcon,
    BarChart as BarChartIcon,
    CheckCircle as CheckCircleIcon,
    Pending as PendingIcon,
    Add as AddIcon,
    SwapHoriz as SwapHorizIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNotify } from 'react-admin';
import axios from 'axios';
import config from '../../config/config.json';

const host = config.production.host;

// Funzione per recuperare il token JWT
const getJwt = () => {
    return localStorage.getItem("feathers-jwt");
};

const getUsersId = () => {
    // parse id in intero
    return parseInt(localStorage.getItem("RaStore.profile.idUser"));
};

// Componente per tab panel
function TabPanel({ children, value, index, ...other }) {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`partita-tabpanel-${index}`}
            aria-labelledby={`partita-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

const PartitaDetailPage = () => {
    const { partitaId } = useParams();
    const navigate = useNavigate();
    const notify = useNotify();

    // Stati principali
    const [partita, setPartita] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tabValue, setTabValue] = useState(0);

    // Stati per i dati delle tab
    const [goalPartita, setGoalPartita] = useState([]);
    const [cartellinoPartita, setCartellinoPartita] = useState([]);
    const [mvpPartita, setMvpPartita] = useState([]);
    const [formazionePartita, setFormazionePartita] = useState([]);
    const [sostituzioniPartita, setSostituzioniPartita] = useState([]);
    const [ruoli, setRuoli] = useState([]);

    // Stati di caricamento per ogni sezione
    const [loadingGoal, setLoadingGoal] = useState(false);
    const [loadingCartellini, setLoadingCartellini] = useState(false);
    const [loadingMvp, setLoadingMvp] = useState(false);
    const [loadingFormazione, setLoadingFormazione] = useState(false);
    const [loadingSostituzioni, setLoadingSostituzioni] = useState(false);

    // Stati per dialog immagine MVP
    const [openImageDialog, setOpenImageDialog] = useState(false);
    const [selectedImage, setSelectedImage] = useState({ url: '', name: '' });

    // Stati per risultati
    const [risultatiPartita, setRisultatiPartita] = useState([]);
    const [loadingRisultati, setLoadingRisultati] = useState(false);
    const [loadingRendiUfficiale, setLoadingRendiUfficiale] = useState(null);

    // Form per nuovo risultato
    const [showAddResultForm, setShowAddResultForm] = useState(false);
    const [newResultForm, setNewResultForm] = useState({
        goalCasa: 0,
        goalOspite: 0,
        note: '',
        flUfficiale: 0
    });
    const [creatingResult, setCreatingResult] = useState(false);

    // Carica dettagli partita
    const loadPartitaDetails = async () => {
        try {
            setLoading(true);
            const jwt = getJwt();

            const response = await axios.get(`${host}/v-partita`, {
                headers: {
                    'Authorization': `Bearer ${jwt}`,
                    'Content-Type': 'application/json'
                },
                params: {
                    partitaId: partitaId,
                    $limit: 1
                }
            });

            if (response.data.data && response.data.data.length > 0) {
                const partitaData = response.data.data[0];

                // Carica foto delle squadre
                const teamIds = [partitaData.teamCasaId, partitaData.teamOspiteId].filter(id => id);
                if (teamIds.length > 0) {
                    const teamsPromises = teamIds.map(teamId =>
                        axios.get(`${host}/tbl-team`, {
                            headers: {
                                'Authorization': `Bearer ${jwt}`,
                                'Content-Type': 'application/json'
                            },
                            params: {
                                id: teamId,
                                $limit: 1
                            }
                        })
                    );

                    const teamsResponses = await Promise.all(teamsPromises);
                    const teams = teamsResponses.flatMap(res => res.data.data || []);

                    const teamCasa = teams.find(t => t.id === partitaData.teamCasaId);
                    const teamOspite = teams.find(t => t.id === partitaData.teamOspiteId);

                    // Arricchisci partita con le foto delle squadre
                    partitaData.teamCasaLogo = teamCasa?.foto || null;
                    partitaData.teamOspiteLogo = teamOspite?.foto || null;
                }

                setPartita(partitaData);
            } else {
                notify("Partita non trovata", { type: 'error' });
                navigate('/partite');
            }
        } catch (error) {
            console.error("Errore nel caricamento partita:", error);
            notify("Errore nel caricamento della partita", { type: 'error' });
            navigate('/partite');
        } finally {
            setLoading(false);
        }
    };

    // Carica ruoli per le formazioni
    const loadRuoli = async () => {
        try {
            const jwt = getJwt();
            const response = await axios.get(`${host}/tbl-ruolo`, {
                headers: {
                    'Authorization': `Bearer ${jwt}`,
                    'Content-Type': 'application/json'
                }
            });
            setRuoli(response.data.data || []);
        } catch (error) {
            console.error("Errore nel caricamento ruoli:", error);
        }
    };

    // Helper per caricare dati utenti
    const loadUsersData = async (userIds) => {
        if (!userIds || userIds.length === 0) return [];

        try {
            const jwt = getJwt();
            const uniqueUserIds = [...new Set(userIds.filter(id => id))];

            const usersPromises = uniqueUserIds.map(userId =>
                axios.get(`${host}/users`, {
                    headers: {
                        'Authorization': `Bearer ${jwt}`,
                        'Content-Type': 'application/json'
                    },
                    params: {
                        id: userId,
                        $limit: 1
                    }
                })
            );

            const usersResponses = await Promise.all(usersPromises);
            return usersResponses.flatMap(response => response.data.data || []);
        } catch (error) {
            console.error('Errore caricamento utenti:', error);
            return [];
        }
    };

    // Carica formazioni della partita
    const loadFormazionePartita = async () => {
        if (!partitaId) return;

        try {
            setLoadingFormazione(true);
            const jwt = getJwt();

            // Carica formazioni
            const formazioneResponse = await axios.get(`${host}/tbl-formazione`, {
                headers: {
                    'Authorization': `Bearer ${jwt}`,
                    'Content-Type': 'application/json'
                },
                params: {
                    partitaId: partitaId,
                    $sort: { ordinamento: 1 }
                }
            });

            const formazioni = formazioneResponse.data.data || [];
            console.log('Formazioni raw:', formazioni); // Debug: verifica campo note

            if (formazioni.length > 0) {
                // Carica dati giocatori
                const playerIds = [...new Set(formazioni.map(f => f.playerId))];
                const playersPromises = playerIds.map(playerId =>
                    axios.get(`${host}/v-player`, {
                        headers: {
                            'Authorization': `Bearer ${jwt}`,
                            'Content-Type': 'application/json'
                        },
                        params: {
                            playerId: playerId,
                            $limit: 1
                        }
                    })
                );

                const playersResponses = await Promise.all(playersPromises);
                const players = playersResponses.flatMap(response => response.data.data || []);

                // Arricchisci formazioni con dati giocatori e ruoli
                const enrichedFormazioni = formazioni.map(formazione => {
                    const player = players.find(p => p.playerId === formazione.playerId);
                    const ruolo = ruoli.find(r => r.id === formazione.ruoloId);

                    const enriched = {
                        ...formazione, // Preserva tutti i campi originali incluso 'note'
                        playerName: player ? player.nomeGiocatore : `Giocatore ${formazione.playerId}`,
                        playerTeam: player ? player.nomeTeam : null,
                        playerFoto: player ? player.foto : null,
                        teamId: player ? player.teamId : null,
                        ruoloNome: ruolo ? ruolo.nome : 'Sconosciuto',
                        ruoloColore: ruolo ? ruolo.colore : '#666'
                    };

                    console.log('Enriched formazione:', enriched); // Debug: verifica campo note dopo enrichment
                    return enriched;
                });

                setFormazionePartita(enrichedFormazioni);
            } else {
                setFormazionePartita([]);
            }
        } catch (error) {
            console.error("Errore nel caricamento formazioni:", error);
            notify("Errore nel caricamento delle formazioni", { type: 'error' });
            setFormazionePartita([]);
        } finally {
            setLoadingFormazione(false);
        }
    };

    // Carica goal della partita
    const loadGoalPartita = async () => {
        if (!partitaId) return;

        try {
            setLoadingGoal(true);
            const jwt = getJwt();

            const response = await axios.get(`${host}/tbl-goal`, {
                headers: {
                    'Authorization': `Bearer ${jwt}`,
                    'Content-Type': 'application/json'
                },
                params: {
                    partitaId: partitaId,
                    $sort: { minuto: 1 }
                }
            });

            const goalData = response.data.data || [];

            if (goalData.length > 0) {
                const playerIds = [...new Set(goalData.map(goal => goal.playerId))];
                const playersPromises = playerIds.map(playerId =>
                    axios.get(`${host}/v-player`, {
                        headers: {
                            'Authorization': `Bearer ${jwt}`,
                            'Content-Type': 'application/json'
                        },
                        params: {
                            playerId: playerId,
                            $limit: 1
                        }
                    })
                );

                const playersResponses = await Promise.all(playersPromises);
                const players = playersResponses.flatMap(response => response.data.data || []);

                // Carica dati utenti
                const userIds = goalData.map(g => g.usersId).filter(id => id);
                const users = await loadUsersData(userIds);

                const enrichedGoal = goalData.map(goal => {
                    const player = players.find(p => p.playerId === goal.playerId);
                    const user = users.find(u => u.id === goal.usersId);

                    return {
                        ...goal,
                        playerName: player ? player.nomeGiocatore : `Giocatore ${goal.playerId}`,
                        playerTeam: player ? player.nomeTeam : null,
                        playerFoto: player ? player.foto : null,
                        usersNome: user ? `${user.nome} ${user.cognome}` : null,
                        usersEmail: user ? user.email : null
                    };
                });

                setGoalPartita(enrichedGoal);
            } else {
                setGoalPartita([]);
            }
        } catch (error) {
            console.error("Errore nel caricamento goal:", error);
            notify("Errore nel caricamento dei goal", { type: 'error' });
            setGoalPartita([]);
        } finally {
            setLoadingGoal(false);
        }
    };

    // Carica cartellini della partita
    const loadCartellinoPartita = async () => {
        if (!partitaId) return;

        try {
            setLoadingCartellini(true);
            const jwt = getJwt();

            const response = await axios.get(`${host}/tbl-cartellino`, {
                headers: {
                    'Authorization': `Bearer ${jwt}`,
                    'Content-Type': 'application/json'
                },
                params: {
                    partitaId: partitaId,
                    $sort: { minuto: 1 }
                }
            });

            const cartellini = response.data.data || [];

            if (cartellini.length > 0) {
                const playerIds = [...new Set(cartellini.map(cartellino => cartellino.playerId))];
                const playersPromises = playerIds.map(playerId =>
                    axios.get(`${host}/v-player`, {
                        headers: {
                            'Authorization': `Bearer ${jwt}`,
                            'Content-Type': 'application/json'
                        },
                        params: {
                            playerId: playerId,
                            $limit: 1
                        }
                    })
                );

                const playersResponses = await Promise.all(playersPromises);
                const players = playersResponses.flatMap(response => response.data.data || []);
                console.log('🟨 Players data from v-player (Cartellini):', players.map(p => ({ playerId: p.playerId, nome: p.nomeGiocatore, foto: p.foto })));

                // Carica tipi cartellino
                const tipoCartellinoIds = [...new Set(cartellini.map(cartellino => cartellino.tipoCartellinoId))];
                const tipoCartellinoPromises = tipoCartellinoIds.map(tipoId =>
                    axios.get(`${host}/tbl-tipocartellino`, {
                        headers: {
                            'Authorization': `Bearer ${jwt}`,
                            'Content-Type': 'application/json'
                        },
                        params: {
                            id: tipoId,
                            $limit: 1
                        }
                    })
                );

                const tipoCartellinoResponses = await Promise.all(tipoCartellinoPromises);
                const tipiCartellino = tipoCartellinoResponses.flatMap(response => response.data.data || []);

                // Carica dati utenti
                const userIds = cartellini.map(c => c.usersId).filter(id => id);
                const users = await loadUsersData(userIds);

                const enrichedCartellini = cartellini.map(cartellino => {
                    const player = players.find(p => p.playerId === cartellino.playerId);
                    const tipoCartellino = tipiCartellino.find(t => t.id === cartellino.tipoCartellinoId);
                    const user = users.find(u => u.id === cartellino.usersId);

                    return {
                        ...cartellino,
                        playerName: player ? player.nomeGiocatore : `Giocatore ${cartellino.playerId}`,
                        playerTeam: player ? player.nomeTeam : null,
                        playerFoto: player ? player.foto : null,
                        teamId: player ? player.teamId : null,
                        tipoCartellinoNome: tipoCartellino ? tipoCartellino.nome : 'SCONOSCIUTO',
                        tipoCartellino: tipoCartellino ? tipoCartellino.nome.toLowerCase() : 'altro',
                        usersNome: user ? `${user.nome} ${user.cognome}` : null,
                        usersEmail: user ? user.email : null
                    };
                });

                setCartellinoPartita(enrichedCartellini);
            } else {
                setCartellinoPartita([]);
            }
        } catch (error) {
            console.error("Errore nel caricamento cartellini:", error);
            notify("Errore nel caricamento dei cartellini", { type: 'error' });
            setCartellinoPartita([]);
        } finally {
            setLoadingCartellini(false);
        }
    };

    // Carica MVP (riutilizzo logica esistente)
    const loadMvpPartita = async () => {
        if (!partitaId) return;

        try {
            setLoadingMvp(true);
            const jwt = getJwt();

            const response = await axios.get(`${host}/tbl-mvp`, {
                headers: {
                    'Authorization': `Bearer ${jwt}`,
                    'Content-Type': 'application/json'
                },
                params: {
                    partitaId: partitaId
                }
            });

            const mvps = response.data.data || [];

            if (mvps.length > 0) {
                const playerIds = [...new Set(mvps.map(mvp => mvp.playerId))];
                const postIds = [...new Set(mvps.map(mvp => mvp.tbl_postsId).filter(id => id))];

                const playersPromises = playerIds.map(playerId =>
                    axios.get(`${host}/v-player`, {
                        headers: {
                            'Authorization': `Bearer ${jwt}`,
                            'Content-Type': 'application/json'
                        },
                        params: {
                            playerId: playerId,
                            $limit: 1
                        }
                    })
                );

                const imagesPromises = postIds.map(postId =>
                    axios.get(`${host}/tbl-image-post`, {
                        headers: {
                            'Authorization': `Bearer ${jwt}`,
                            'Content-Type': 'application/json'
                        },
                        params: {
                            tbl_postsId: postId,
                            $limit: 1
                        }
                    })
                );

                const postsPromises = postIds.map(postId =>
                    axios.get(`${host}/tbl-posts`, {
                        headers: {
                            'Authorization': `Bearer ${jwt}`,
                            'Content-Type': 'application/json'
                        },
                        params: {
                            id: postId,
                            $limit: 1
                        }
                    })
                );

                const [playersResponses, imagesResponses, postsResponses] = await Promise.all([
                    Promise.all(playersPromises),
                    Promise.all(imagesPromises),
                    Promise.all(postsPromises)
                ]);

                const players = playersResponses.flatMap(response => response.data.data || []);
                const images = imagesResponses.flatMap(response => response.data.data || []);
                const posts = postsResponses.flatMap(response => response.data.data || []);

                const enrichedMvps = mvps.map(mvp => {
                    const player = players.find(p => p.playerId === mvp.playerId);
                    const image = images.find(img => img.tbl_postsId === mvp.tbl_postsId);
                    const post = posts.find(p => p.id === mvp.tbl_postsId);

                    // Converti base64 BLOB in URL dati
                    let imageUrl = null;
                    if (image && image.base64Image) {
                        let base64String;

                        if (typeof image.base64Image === 'string') {
                            base64String = image.base64Image;
                        } else if (image.base64Image && typeof image.base64Image === 'object') {
                            // Se è un oggetto Buffer con array di byte
                            if (image.base64Image.type === 'Buffer' && Array.isArray(image.base64Image.data)) {
                                // I byte sono già i caratteri ASCII della stringa base64
                                // Converti array di byte in stringa usando chunks per evitare stack overflow
                                const bytes = new Uint8Array(image.base64Image.data);
                                let base64Chars = '';
                                const chunkSize = 8192; // Processa 8KB alla volta

                                for (let i = 0; i < bytes.length; i += chunkSize) {
                                    const chunk = bytes.slice(i, i + chunkSize);
                                    base64Chars += String.fromCharCode.apply(null, chunk);
                                }

                                base64String = base64Chars; // È già base64, non serve btoa()
                            } else if (image.base64Image.data) {
                                base64String = image.base64Image.data;
                            } else {
                                // Converti oggetto in stringa JSON come fallback
                                base64String = JSON.stringify(image.base64Image);
                            }
                        } else {
                            base64String = String(image.base64Image);
                        }

                        // Verifica che sia una stringa valida prima di usare startsWith
                        if (typeof base64String === 'string' && base64String.trim()) {
                            imageUrl = base64String.startsWith('data:')
                                ? base64String
                                : `data:image/jpeg;base64,${base64String}`;
                        }
                    }

                    return {
                        ...mvp,
                        playerName: player ? player.nomeGiocatore : `Giocatore ${mvp.playerId}`,
                        playerTeam: player ? player.nomeTeam : null,
                        playerTeamId: player ? player.teamId : null,
                        playerFoto: player ? player.foto : null,
                        mvpImage: imageUrl,
                        postId: mvp.tbl_postsId,
                        usersId: post ? post.usersId : null
                    };
                });

                setMvpPartita(enrichedMvps);
            } else {
                setMvpPartita([]);
            }
        } catch (error) {
            console.error("Errore nel caricamento MVP:", error);
            console.error("Dettagli errore MVP:", {
                message: error.message,
                status: error.response?.status,
                statusText: error.response?.statusText,
                data: error.response?.data,
                partitaId: partitaId,
                url: `${host}/tbl-mvp`
            });
            notify("Errore nel caricamento degli MVP", { type: 'error' });
            setMvpPartita([]);
        } finally {
            setLoadingMvp(false);
        }
    };

    // Carica sostituzioni della partita
    const loadSostituzioniPartita = async () => {
        if (!partitaId) return;

        try {
            setLoadingSostituzioni(true);
            const jwt = getJwt();

            const response = await axios.get(`${host}/tbl-sostituzione`, {
                headers: {
                    'Authorization': `Bearer ${jwt}`,
                    'Content-Type': 'application/json'
                },
                params: {
                    partitaId: partitaId,
                    $sort: { minuto: 1 }
                }
            });

            const sostituzioni = response.data.data || [];

            if (sostituzioni.length > 0) {
                // Carica dati di tutti i giocatori coinvolti (entranti e uscenti)
                const playerIds = [...new Set([
                    ...sostituzioni.map(s => s.playerEntranteId),
                    ...sostituzioni.map(s => s.playerUscenteId)
                ])];

                const playersPromises = playerIds.map(playerId =>
                    axios.get(`${host}/v-player`, {
                        headers: {
                            'Authorization': `Bearer ${jwt}`,
                            'Content-Type': 'application/json'
                        },
                        params: {
                            playerId: playerId,
                            $limit: 1
                        }
                    })
                );

                const playersResponses = await Promise.all(playersPromises);
                const players = playersResponses.flatMap(response => response.data.data || []);
                console.log('🔄 Players data from v-player (Sostituzioni):', players.map(p => ({ playerId: p.playerId, nome: p.nomeGiocatore, foto: p.foto })));

                // Carica dati utenti
                const userIds = sostituzioni.map(s => s.usersId).filter(id => id);
                const users = await loadUsersData(userIds);

                const enrichedSostituzioni = sostituzioni.map(sost => {
                    const playerEntrante = players.find(p => p.playerId === sost.playerEntranteId);
                    const playerUscente = players.find(p => p.playerId === sost.playerUscenteId);

                    const user = users.find(u => u.id === sost.usersId);

                    return {
                        ...sost,
                        // Usa il teamId del giocatore entrante (sia entrante che uscente sono della stessa squadra)
                        teamId: playerEntrante ? playerEntrante.teamId : (playerUscente ? playerUscente.teamId : sost.teamId),
                        playerEntranteNome: playerEntrante ? playerEntrante.nomeGiocatore : `Giocatore ${sost.playerEntranteId}`,
                        playerEntranteFoto: playerEntrante ? playerEntrante.foto : null,
                        playerEntranteTeam: playerEntrante ? playerEntrante.nomeTeam : null,
                        playerUscenteNome: playerUscente ? playerUscente.nomeGiocatore : `Giocatore ${sost.playerUscenteId}`,
                        playerUscenteFoto: playerUscente ? playerUscente.foto : null,
                        playerUscenteTeam: playerUscente ? playerUscente.nomeTeam : null,
                        usersNome: user ? `${user.nome} ${user.cognome}` : null,
                        usersEmail: user ? user.email : null
                    };
                });

                setSostituzioniPartita(enrichedSostituzioni);
            } else {
                setSostituzioniPartita([]);
            }
        } catch (error) {
            console.error("Errore nel caricamento sostituzioni:", error);
            notify("Errore nel caricamento delle sostituzioni", { type: 'error' });
            setSostituzioniPartita([]);
        } finally {
            setLoadingSostituzioni(false);
        }
    };

    // Carica risultati partita
    const loadRisultatiPartita = async () => {
        if (!partitaId) return;

        try {
            setLoadingRisultati(true);
            const jwt = getJwt();
            const response = await axios.get(`${host}/proc-get-risultato`, {
                headers: { 'Authorization': `Bearer ${jwt}` },
                params: {
                    partitaId: partitaId
                }
            });

            const dataObj = response.data.data || {};
            const risultatiArray = Object.values(dataObj).sort((a, b) => a.Id - b.Id);
            setRisultatiPartita(risultatiArray);

        } catch (error) {
            console.error("Errore nel caricamento risultati:", error);
            notify("Errore nel caricamento dei risultati", { type: 'error' });
            setRisultatiPartita([]);
        } finally {
            setLoadingRisultati(false);
        }
    };

    // Crea nuovo risultato
    const handleCreaRisultato = async () => {
        if (!partitaId) return;

        try {
            setCreatingResult(true);
            const jwt = getJwt();

            const response = await axios.get(
                `${host}/dynamic-proc`,
                {
                    headers: { 'Authorization': `Bearer ${jwt}` },
                    params: {
                        proc_name: 'proc_crea_risultato',
                        in_usersId: getUsersId(),
                        in_partitaId: parseInt(partitaId),
                        in_goalCasa: parseInt(newResultForm.goalCasa),
                        in_goalOspite: parseInt(newResultForm.goalOspite),
                        in_note: newResultForm.note || null,
                        in_flUfficiale: parseInt(newResultForm.flUfficiale)
                    }
                }
            );

            if (response.data.output_params?.out_esito === 1) {
                const message = response.data.output_params.out_msg || 'Risultato creato con successo!';
                notify(message, { type: 'success' });

                // Reset form
                setNewResultForm({
                    goalCasa: 0,
                    goalOspite: 0,
                    note: '',
                    flUfficiale: 0
                });
                setShowAddResultForm(false);

                // Ricarica risultati e dettagli partita
                loadRisultatiPartita();
                loadPartitaDetails();
            }

        } catch (error) {
            console.error("Errore nella creazione del risultato:", error);
            const errorMessage = error.response?.data?.message || error.message || "Errore sconosciuto";
            notify(`Errore: ${errorMessage}`, { type: 'error' });
        } finally {
            setCreatingResult(false);
        }
    };

    // Rendi ufficiale un risultato
    const handleRendiUfficiale = async (risultatoId) => {
        try {
            setLoadingRendiUfficiale(risultatoId);
            const jwt = getJwt();

            const response = await axios.patch(
                `${host}/proc-imposta-risultato-ufficiale/${risultatoId}`,
                {},
                {
                    headers: { 'Authorization': `Bearer ${jwt}` }
                }
            );

            if (response.data.esito === 1) {
                notify(
                    `Risultato reso ufficiale! ${response.data.operazioni_effettuate?.altri_risultati_disattivati || 0} altri risultati disattivati`,
                    { type: 'success' }
                );

                // Ricarica risultati e dettagli partita
                loadRisultatiPartita();
                loadPartitaDetails();
            }

        } catch (error) {
            console.error("Errore nel rendere ufficiale il risultato:", error);
            const errorMessage = error.response?.data?.message || error.message || "Errore sconosciuto";
            notify(`Errore: ${errorMessage}`, { type: 'error' });
        } finally {
            setLoadingRendiUfficiale(null);
        }
    };

    // Gestione dialog immagine MVP
    const handleOpenImageDialog = (imageUrl, playerName) => {
        setSelectedImage({ url: imageUrl, name: playerName });
        setOpenImageDialog(true);
    };

    const handleCloseImageDialog = () => {
        setOpenImageDialog(false);
        setSelectedImage({ url: '', name: '' });
    };

    // Gestione cambio tab
    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);

        // Carica dati quando si apre una tab per la prima volta
        if (newValue === 1 && formazionePartita.length === 0) {
            loadFormazionePartita();
        } else if (newValue === 2 && goalPartita.length === 0 && cartellinoPartita.length === 0 && sostituzioniPartita.length === 0) {
            loadGoalPartita();
            loadCartellinoPartita();
            loadSostituzioniPartita();
        } else if (newValue === 3 && mvpPartita.length === 0) {
            loadMvpPartita();
        } else if (newValue === 4 && risultatiPartita.length === 0) {
            loadRisultatiPartita();
        }
    };

    // Effects
    useEffect(() => {
        loadPartitaDetails();
        loadRuoli();
    }, [partitaId]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress size={40} />
            </Box>
        );
    }

    if (!partita) {
        return (
            <Alert severity="error" sx={{ m: 3 }}>
                Partita non trovata
            </Alert>
        );
    }

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f6f7fb', minHeight: '100vh' }}>
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>

                {/* Header con navigazione */}
                <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Tooltip title="Torna alle partite">
                        <IconButton onClick={() => navigate('/partite')} sx={{ bgcolor: 'white', boxShadow: 1 }}>
                            <ArrowBackIcon />
                        </IconButton>
                    </Tooltip>

                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                            ⚽ Dettaglio Partita
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {partita.teamCasaNome} vs {partita.teamOspiteNome} • {partita.legaNome}
                        </Typography>
                    </Box>
                </Box>

                {/* Card principale con tab */}
                <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
                    {/* Header della partita */}
                    <CardContent sx={{ pb: 0 }}>
                        <Grid container spacing={3} alignItems="center">
                            {/* Squadra Casa */}
                            <Grid item xs={4}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Avatar
                                        src={partita.teamCasaLogo}
                                        sx={{ width: 60, height: 60, mx: 'auto', mb: 1 }}
                                    >
                                        {partita.teamCasaNome?.charAt(0)}
                                    </Avatar>
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        {partita.teamCasaNome}
                                    </Typography>
                                </Box>
                            </Grid>

                            {/* Risultato centrale */}
                            <Grid item xs={4}>
                                <Box sx={{ textAlign: 'center' }}>
                                    {partita.flDisputata ? (
                                        <Typography variant="h3" sx={{ fontWeight: 700, color: '#1976d2' }}>
                                            {partita.goalCasa || 0} - {partita.goalOspite || 0}
                                        </Typography>
                                    ) : (
                                        <Typography variant="h5" color="text.secondary">
                                            Da giocare
                                        </Typography>
                                    )}

                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        {partita.data ? new Date(partita.data).toLocaleDateString('it-IT') : 'Data TBD'}
                                        {partita.ore && ` • ${partita.ore}`}
                                    </Typography>

                                    {partita.luogo && (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                            📍 {partita.luogo}
                                        </Typography>
                                    )}
                                </Box>
                            </Grid>

                            {/* Squadra Ospite */}
                            <Grid item xs={4}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Avatar
                                        src={partita.teamOspiteLogo}
                                        sx={{ width: 60, height: 60, mx: 'auto', mb: 1 }}
                                    >
                                        {partita.teamOspiteNome?.charAt(0)}
                                    </Avatar>
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        {partita.teamOspiteNome}
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>

                        {/* Informazioni aggiuntive */}
                        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                            <Chip
                                label={`${partita.legaNome} • ${partita.stagioneNome}`}
                                color="primary"
                                variant="outlined"
                            />
                            <Chip
                                label={`Giornata ${partita.giornataNumero || '?'}`}
                                color="secondary"
                                variant="outlined"
                            />
                            {partita.flDisputata ? (
                                <Chip label="Disputata" color="success" />
                            ) : (
                                <Chip label="Da giocare" color="default" />
                            )}
                        </Box>
                    </CardContent>

                    {/* Tab Navigation */}
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
                        <Tabs value={tabValue} onChange={handleTabChange} aria-label="partita tabs">
                            <Tab
                                icon={<InfoIcon />}
                                label="Informazioni"
                                iconPosition="start"
                                sx={{ textTransform: 'none', fontWeight: 600 }}
                            />
                            <Tab
                                icon={<GroupsIcon />}
                                label="Formazioni"
                                iconPosition="start"
                                sx={{ textTransform: 'none', fontWeight: 600 }}
                            />
                            <Tab
                                icon={<TimelineIcon />}
                                label="Eventi"
                                iconPosition="start"
                                sx={{ textTransform: 'none', fontWeight: 600 }}
                            />
                            <Tab
                                icon={<EmojiEventsIcon />}
                                label="MVP"
                                iconPosition="start"
                                sx={{ textTransform: 'none', fontWeight: 600 }}
                            />
                            <Tab
                                icon={<SportsSoccerIcon />}
                                label="Risultati"
                                iconPosition="start"
                                sx={{ textTransform: 'none', fontWeight: 600 }}
                            />
                        </Tabs>
                    </Box>

                    {/* Tab Content */}
                    <TabPanel value={tabValue} index={0}>
                        {/* Tab Informazioni - Placeholder per ora */}
                        <Typography variant="h6" gutterBottom>
                            📊 Informazioni Partita
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Contenuto informazioni dettagliate...
                        </Typography>
                    </TabPanel>

                    <TabPanel value={tabValue} index={1}>
                        {/* Tab Formazioni */}
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <GroupsIcon sx={{ fontSize: 24, color: '#1976d2' }} />
                            Formazioni
                        </Typography>

                        {loadingFormazione ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                <CircularProgress />
                            </Box>
                        ) : formazionePartita.length > 0 ? (
                            <Grid container spacing={3}>
                                {/* Formazione Casa */}
                                <Grid item xs={12} md={6}>
                                    <Card sx={{ borderRadius: 2, border: '2px solid #1976d2' }}>
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                color: '#1976d2',
                                                fontWeight: 600
                                            }}>
                                                <Avatar src={partita.teamCasaLogo} sx={{ width: 32, height: 32 }}>
                                                    {partita.teamCasaNome?.charAt(0)}
                                                </Avatar>
                                                {partita.teamCasaNome}
                                            </Typography>

                                            {formazionePartita
                                                .filter(f => f.teamId === partita.teamCasaId)
                                                .sort((a, b) => (a.ordinamento || 0) - (b.ordinamento || 0))
                                                .map((giocatore, index) => (
                                                    <Box key={giocatore.id} sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 2,
                                                        p: 1.5,
                                                        mb: 1,
                                                        bgcolor: index % 2 === 0 ? '#f8f9fa' : 'transparent',
                                                        borderRadius: 1,
                                                        border: '1px solid #e0e0e0'
                                                    }}>
                                                        <Avatar src={giocatore.playerFoto} sx={{ width: 40, height: 40 }}>
                                                            {giocatore.playerName?.charAt(0)}
                                                        </Avatar>

                                                        <Box sx={{ flex: 1 }}>
                                                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                                                {giocatore.playerName}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {giocatore.ruoloNome}
                                                            </Typography>
                                                            <br></br>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {giocatore.note}
                                                            </Typography>
                                                        </Box>

                                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                            <Chip
                                                                label={giocatore.ruoloNome}
                                                                size="small"
                                                                sx={{
                                                                    bgcolor: giocatore.ruoloColore || '#666',
                                                                    color: 'white',
                                                                    fontWeight: 600
                                                                }}
                                                            />

                                                            {giocatore.titolare === 1 ? (
                                                                <Chip
                                                                    icon={<CheckCircleIcon />}
                                                                    label="Titolare"
                                                                    size="small"
                                                                    color="success"
                                                                    sx={{ fontWeight: 600 }}
                                                                />
                                                            ) : (
                                                                <Chip
                                                                    label="Riserva"
                                                                    size="small"
                                                                    variant="outlined"
                                                                    sx={{ borderColor: '#999', color: '#666' }}
                                                                />
                                                            )}
                                                        </Box>
                                                    </Box>
                                                ))
                                            }

                                            {formazionePartita.filter(f => f.teamId === partita.teamCasaId).length === 0 && (
                                                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', p: 2 }}>
                                                    Nessun giocatore in formazione
                                                </Typography>
                                            )}
                                        </CardContent>
                                    </Card>
                                </Grid>

                                {/* Formazione Ospite */}
                                <Grid item xs={12} md={6}>
                                    <Card sx={{ borderRadius: 2, border: '2px solid #d32f2f' }}>
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                color: '#d32f2f',
                                                fontWeight: 600
                                            }}>
                                                <Avatar src={partita.teamOspiteLogo} sx={{ width: 32, height: 32 }}>
                                                    {partita.teamOspiteNome?.charAt(0)}
                                                </Avatar>
                                                {partita.teamOspiteNome}
                                            </Typography>

                                            {formazionePartita
                                                .filter(f => f.teamId === partita.teamOspiteId)
                                                .sort((a, b) => (a.ordinamento || 0) - (b.ordinamento || 0))
                                                .map((giocatore, index) => (
                                                    <Box key={giocatore.id} sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 2,
                                                        p: 1.5,
                                                        mb: 1,
                                                        bgcolor: index % 2 === 0 ? '#f8f9fa' : 'transparent',
                                                        borderRadius: 1,
                                                        border: '1px solid #e0e0e0'
                                                    }}>
                                                        <Avatar src={giocatore.playerFoto} sx={{ width: 40, height: 40 }}>
                                                            {giocatore.playerName?.charAt(0)}
                                                        </Avatar>

                                                        <Box sx={{ flex: 1 }}>
                                                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                                                {giocatore.playerName}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {giocatore.ruoloNome}
                                                            </Typography>
                                                            <br></br>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {giocatore.note}
                                                            </Typography>
                                                        </Box>

                                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                            <Chip
                                                                label={giocatore.ruoloNome}
                                                                size="small"
                                                                sx={{
                                                                    bgcolor: giocatore.ruoloColore || '#666',
                                                                    color: 'white',
                                                                    fontWeight: 600
                                                                }}
                                                            />

                                                            {giocatore.titolare === 1 ? (
                                                                <Chip
                                                                    icon={<CheckCircleIcon />}
                                                                    label="Titolare"
                                                                    size="small"
                                                                    color="success"
                                                                    sx={{ fontWeight: 600 }}
                                                                />
                                                            ) : (
                                                                <Chip
                                                                    label="Riserva"
                                                                    size="small"
                                                                    variant="outlined"
                                                                    sx={{ borderColor: '#999', color: '#666' }}
                                                                />
                                                            )}
                                                        </Box>
                                                    </Box>
                                                ))
                                            }

                                            {formazionePartita.filter(f => f.teamId === partita.teamOspiteId).length === 0 && (
                                                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', p: 2 }}>
                                                    Nessun giocatore in formazione
                                                </Typography>
                                            )}
                                        </CardContent>
                                    </Card>
                                </Grid>

                                {/* Statistiche formazioni */}
                                <Grid item xs={12}>
                                    <Paper sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                                            📊 Statistiche Formazioni
                                        </Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={6} md={3}>
                                                <Box sx={{ textAlign: 'center' }}>
                                                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1976d2' }}>
                                                        {formazionePartita.filter(f => f.teamId === partita.teamCasaId).length}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Giocatori Casa
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={6} md={3}>
                                                <Box sx={{ textAlign: 'center' }}>
                                                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#d32f2f' }}>
                                                        {formazionePartita.filter(f => f.teamId === partita.teamOspiteId).length}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Giocatori Ospite
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={6} md={3}>
                                                <Box sx={{ textAlign: 'center' }}>
                                                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#2e7d32' }}>
                                                        {formazionePartita.length}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Totale Giocatori
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={6} md={3}>
                                                <Box sx={{ textAlign: 'center' }}>
                                                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#ff9800' }}>
                                                        {[...new Set(formazionePartita.map(f => f.ruoloId))].length}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Ruoli Diversi
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                </Grid>
                            </Grid>
                        ) : (
                            <Alert severity="info" sx={{ mt: 2 }}>
                                <Typography variant="body2">
                                    📋 <strong>Nessuna formazione disponibile</strong><br />
                                    Non sono state ancora definite le formazioni per questa partita.
                                </Typography>
                            </Alert>
                        )}
                    </TabPanel>

                    <TabPanel value={tabValue} index={2}>
                        {/* Tab Eventi */}
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TimelineIcon sx={{ fontSize: 24, color: '#1976d2' }} />
                            Eventi della Partita
                        </Typography>

                        {(loadingGoal || loadingCartellini || loadingSostituzioni) ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                <CircularProgress />
                            </Box>
                        ) : (goalPartita.length > 0 || cartellinoPartita.length > 0 || sostituzioniPartita.length > 0) ? (
                            <Grid container spacing={3}>
                                {/* Sezione Goal - Squadra Casa */}
                                {(() => {
                                    const goalCasa = goalPartita.filter(g => g.teamId === partita.teamCasaId);
                                    return goalCasa.length > 0 && (
                                        <Grid item xs={12} md={6}>
                                            <Card sx={{ borderRadius: 2, border: '2px solid #1976d2' }}>
                                                <CardContent>
                                                    <Typography variant="h6" gutterBottom sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                        color: '#1976d2',
                                                        fontWeight: 600
                                                    }}>
                                                        <SportsSoccerIcon sx={{ fontSize: 24 }} />
                                                        Goal {partita.teamCasaNome} ({goalCasa.length})
                                                    </Typography>

                                                    {goalCasa.map((goal, index) => {
                                                        const isAutogoal = goal.autogoal === 1 || goal.autogoal === true;
                                                        return (
                                                            <Box key={goal.id} sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 2,
                                                                p: 2,
                                                                mb: 1,
                                                                bgcolor: isAutogoal ? '#ffebee' : (index % 2 === 0 ? '#e3f2fd' : 'transparent'),
                                                                borderRadius: 1,
                                                                border: isAutogoal ? '2px solid #d32f2f' : '1px solid #bbdefb'
                                                            }}>
                                                                <Box sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 1,
                                                                    minWidth: 80
                                                                }}>
                                                                    <AccessTimeIcon sx={{ fontSize: 16, color: '#666' }} />
                                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                        {goal.minuto}'
                                                                    </Typography>
                                                                </Box>

                                                                <SportsSoccerIcon sx={{ fontSize: 20, color: isAutogoal ? '#d32f2f' : '#1976d2' }} />

                                                                <Tooltip title={goal.usersNome ? `Inserito da: ${goal.usersNome}${goal.usersEmail ? ` (${goal.usersEmail})` : ''}` : 'Utente non disponibile'} arrow>
                                                                    <Avatar src={goal.playerFoto} sx={{ width: 40, height: 40 }}>
                                                                        {goal.playerName?.charAt(0)}
                                                                    </Avatar>
                                                                </Tooltip>

                                                                <Box sx={{ flex: 1 }}>
                                                                    <Typography variant="body1" sx={{
                                                                        fontWeight: 600,
                                                                        color: isAutogoal ? '#d32f2f' : 'inherit'
                                                                    }}>
                                                                        {isAutogoal ? '🔴 AUTOGOAL - ' : '⚽ '}{goal.playerName}
                                                                    </Typography>
                                                                    {goal.playerTeam && (
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            {goal.playerTeam}
                                                                        </Typography>
                                                                    )}
                                                                    {goal.note && (
                                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                                                                            💬 {goal.note}
                                                                        </Typography>
                                                                    )}
                                                                </Box>

                                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                                    {isAutogoal && (
                                                                        <Chip
                                                                            label="Autogoal"
                                                                            size="small"
                                                                            sx={{
                                                                                bgcolor: '#ffcdd2',
                                                                                color: '#d32f2f',
                                                                                fontWeight: 600
                                                                            }}
                                                                        />
                                                                    )}
                                                                    {goal.flAnnullato ? (
                                                                        <Chip label="Annullato" size="small" color="error" variant="outlined" />
                                                                    ) : (
                                                                        <Chip label="Valido" size="small" color="success" variant="outlined" />
                                                                    )}
                                                                    {!goal.flVisibile && (
                                                                        <Chip label="Nascosto" size="small" color="warning" variant="outlined" />
                                                                    )}
                                                                </Box>
                                                            </Box>
                                                        );
                                                    })}
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    );
                                })()}

                                {/* Sezione Goal - Squadra Ospite */}
                                {(() => {
                                    const goalOspite = goalPartita.filter(g => g.teamId === partita.teamOspiteId);
                                    return goalOspite.length > 0 && (
                                        <Grid item xs={12} md={6}>
                                            <Card sx={{ borderRadius: 2, border: '2px solid #d32f2f' }}>
                                                <CardContent>
                                                    <Typography variant="h6" gutterBottom sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                        color: '#d32f2f',
                                                        fontWeight: 600
                                                    }}>
                                                        <SportsSoccerIcon sx={{ fontSize: 24 }} />
                                                        Goal {partita.teamOspiteNome} ({goalOspite.length})
                                                    </Typography>

                                                    {goalOspite.map((goal, index) => {
                                                        const isAutogoal = goal.autogoal === 1 || goal.autogoal === true;
                                                        return (
                                                            <Box key={goal.id} sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 2,
                                                                p: 2,
                                                                mb: 1,
                                                                bgcolor: isAutogoal ? '#ffebee' : (index % 2 === 0 ? '#ffebee' : 'transparent'),
                                                                borderRadius: 1,
                                                                border: isAutogoal ? '2px solid #d32f2f' : '1px solid #ef9a9a'
                                                            }}>
                                                                <Box sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 1,
                                                                    minWidth: 80
                                                                }}>
                                                                    <AccessTimeIcon sx={{ fontSize: 16, color: '#666' }} />
                                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                        {goal.minuto}'
                                                                    </Typography>
                                                                </Box>

                                                                <SportsSoccerIcon sx={{ fontSize: 20, color: isAutogoal ? '#b71c1c' : '#d32f2f' }} />

                                                                <Tooltip title={goal.usersNome ? `Inserito da: ${goal.usersNome}${goal.usersEmail ? ` (${goal.usersEmail})` : ''}` : 'Utente non disponibile'} arrow>
                                                                    <Avatar src={goal.playerFoto} sx={{ width: 40, height: 40 }}>
                                                                        {goal.playerName?.charAt(0)}
                                                                    </Avatar>
                                                                </Tooltip>

                                                                <Box sx={{ flex: 1 }}>
                                                                    <Typography variant="body1" sx={{
                                                                        fontWeight: 600,
                                                                        color: isAutogoal ? '#b71c1c' : 'inherit'
                                                                    }}>
                                                                        {isAutogoal ? '🔴 AUTOGOAL - ' : '⚽ '}{goal.playerName}
                                                                    </Typography>
                                                                    {goal.playerTeam && (
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            {goal.playerTeam}
                                                                        </Typography>
                                                                    )}
                                                                    {goal.note && (
                                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                                                                            💬 {goal.note}
                                                                        </Typography>
                                                                    )}
                                                                </Box>

                                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                                    {isAutogoal && (
                                                                        <Chip
                                                                            label="Autogoal"
                                                                            size="small"
                                                                            sx={{
                                                                                bgcolor: '#ffcdd2',
                                                                                color: '#b71c1c',
                                                                                fontWeight: 600
                                                                            }}
                                                                        />
                                                                    )}
                                                                    {goal.flAnnullato ? (
                                                                        <Chip label="Annullato" size="small" color="error" variant="outlined" />
                                                                    ) : (
                                                                        <Chip label="Valido" size="small" color="success" variant="outlined" />
                                                                    )}
                                                                    {!goal.flVisibile && (
                                                                        <Chip label="Nascosto" size="small" color="warning" variant="outlined" />
                                                                    )}
                                                                </Box>
                                                            </Box>
                                                        );
                                                    })}
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    );
                                })()}

                                {/* Sezione Cartellini - Squadra Casa */}
                                {(() => {
                                    const cartellinoCasa = cartellinoPartita.filter(c => c.teamId === partita.teamCasaId);
                                    return cartellinoCasa.length > 0 && (
                                        <Grid item xs={12} md={6}>
                                            <Card sx={{ borderRadius: 2, border: '2px solid #1976d2' }}>
                                                <CardContent>
                                                    <Typography variant="h6" gutterBottom sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                        color: '#1976d2',
                                                        fontWeight: 600
                                                    }}>
                                                        <CropSquareIcon sx={{ fontSize: 24 }} />
                                                        Cartellini {partita.teamCasaNome} ({cartellinoCasa.length})
                                                    </Typography>

                                                    {cartellinoCasa.map((cartellino, index) => (
                                                        <Box key={cartellino.id} sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 2,
                                                            p: 2,
                                                            mb: 1,
                                                            bgcolor: index % 2 === 0 ? '#e3f2fd' : 'transparent',
                                                            borderRadius: 1,
                                                            border: '1px solid #bbdefb'
                                                        }}>
                                                            <Box sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 1,
                                                                minWidth: 80
                                                            }}>
                                                                <AccessTimeIcon sx={{ fontSize: 16, color: '#666' }} />
                                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                    {cartellino.minuto}'
                                                                </Typography>
                                                            </Box>

                                                            {/* Icona cartellino giallo o rosso */}
                                                            {cartellino.tipoCartellino?.includes('giallo') ? (
                                                                <CropSquareIcon sx={{ fontSize: 18, color: '#ffc107' }} />
                                                            ) : cartellino.tipoCartellino?.includes('rosso') ? (
                                                                <StopIcon sx={{ fontSize: 18, color: '#d32f2f' }} />
                                                            ) : (
                                                                <CropSquareIcon sx={{ fontSize: 18, color: '#666' }} />
                                                            )}

                                                            <Tooltip title={cartellino.usersNome ? `Inserito da: ${cartellino.usersNome}${cartellino.usersEmail ? ` (${cartellino.usersEmail})` : ''}` : 'Utente non disponibile'} arrow>
                                                                <Avatar src={cartellino.playerFoto} sx={{ width: 40, height: 40 }}>
                                                                    {cartellino.playerName?.charAt(0)}
                                                                </Avatar>
                                                            </Tooltip>

                                                            <Box sx={{ flex: 1 }}>
                                                                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                                                    {cartellino.playerName}
                                                                </Typography>
                                                                {cartellino.playerTeam && (
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {cartellino.playerTeam}
                                                                    </Typography>
                                                                )}
                                                                {cartellino.note && (
                                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                                                                        💬 {cartellino.note}
                                                                    </Typography>
                                                                )}
                                                            </Box>

                                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                                <Chip
                                                                    label={cartellino.tipoCartellinoNome || cartellino.tipoCartellino || 'Altro'}
                                                                    size="small"
                                                                    sx={{
                                                                        bgcolor: cartellino.tipoCartellino?.includes('giallo') ? '#fff3c4' : cartellino.tipoCartellino?.includes('rosso') ? '#ffebee' : '#f5f5f5',
                                                                        color: cartellino.tipoCartellino?.includes('giallo') ? '#f57c00' : cartellino.tipoCartellino?.includes('rosso') ? '#d32f2f' : '#666',
                                                                        fontWeight: 600
                                                                    }}
                                                                />
                                                                {cartellino.flAnnullato ? (
                                                                    <Chip label="Annullato" size="small" color="error" variant="outlined" />
                                                                ) : (
                                                                    <Chip label="Valido" size="small" color="success" variant="outlined" />
                                                                )}
                                                                {!cartellino.flVisibile && (
                                                                    <Chip label="Nascosto" size="small" color="warning" variant="outlined" />
                                                                )}
                                                            </Box>
                                                        </Box>
                                                    ))}
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    );
                                })()}

                                {/* Sezione Cartellini - Squadra Ospite */}
                                {(() => {
                                    const cartellinoOspite = cartellinoPartita.filter(c => c.teamId === partita.teamOspiteId);
                                    return cartellinoOspite.length > 0 && (
                                        <Grid item xs={12} md={6}>
                                            <Card sx={{ borderRadius: 2, border: '2px solid #d32f2f' }}>
                                                <CardContent>
                                                    <Typography variant="h6" gutterBottom sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                        color: '#d32f2f',
                                                        fontWeight: 600
                                                    }}>
                                                        <CropSquareIcon sx={{ fontSize: 24 }} />
                                                        Cartellini {partita.teamOspiteNome} ({cartellinoOspite.length})
                                                    </Typography>

                                                    {cartellinoOspite.map((cartellino, index) => (
                                                        <Box key={cartellino.id} sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 2,
                                                            p: 2,
                                                            mb: 1,
                                                            bgcolor: index % 2 === 0 ? '#ffebee' : 'transparent',
                                                            borderRadius: 1,
                                                            border: '1px solid #ef9a9a'
                                                        }}>
                                                            <Box sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 1,
                                                                minWidth: 80
                                                            }}>
                                                                <AccessTimeIcon sx={{ fontSize: 16, color: '#666' }} />
                                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                    {cartellino.minuto}'
                                                                </Typography>
                                                            </Box>

                                                            {/* Icona cartellino giallo o rosso */}
                                                            {cartellino.tipoCartellino?.includes('giallo') ? (
                                                                <CropSquareIcon sx={{ fontSize: 18, color: '#ffc107' }} />
                                                            ) : cartellino.tipoCartellino?.includes('rosso') ? (
                                                                <StopIcon sx={{ fontSize: 18, color: '#d32f2f' }} />
                                                            ) : (
                                                                <CropSquareIcon sx={{ fontSize: 18, color: '#666' }} />
                                                            )}

                                                            <Tooltip title={cartellino.usersNome ? `Inserito da: ${cartellino.usersNome}${cartellino.usersEmail ? ` (${cartellino.usersEmail})` : ''}` : 'Utente non disponibile'} arrow>
                                                                <Avatar src={cartellino.playerFoto} sx={{ width: 40, height: 40 }}>
                                                                    {cartellino.playerName?.charAt(0)}
                                                                </Avatar>
                                                            </Tooltip>

                                                            <Box sx={{ flex: 1 }}>
                                                                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                                                    {cartellino.playerName}
                                                                </Typography>
                                                                {cartellino.playerTeam && (
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {cartellino.playerTeam}
                                                                    </Typography>
                                                                )}
                                                                {cartellino.note && (
                                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                                                                        💬 {cartellino.note}
                                                                    </Typography>
                                                                )}
                                                            </Box>

                                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                                <Chip
                                                                    label={cartellino.tipoCartellinoNome || cartellino.tipoCartellino || 'Altro'}
                                                                    size="small"
                                                                    sx={{
                                                                        bgcolor: cartellino.tipoCartellino?.includes('giallo') ? '#fff3c4' : cartellino.tipoCartellino?.includes('rosso') ? '#ffebee' : '#f5f5f5',
                                                                        color: cartellino.tipoCartellino?.includes('giallo') ? '#f57c00' : cartellino.tipoCartellino?.includes('rosso') ? '#d32f2f' : '#666',
                                                                        fontWeight: 600
                                                                    }}
                                                                />
                                                                {cartellino.flAnnullato ? (
                                                                    <Chip label="Annullato" size="small" color="error" variant="outlined" />
                                                                ) : (
                                                                    <Chip label="Valido" size="small" color="success" variant="outlined" />
                                                                )}
                                                                {!cartellino.flVisibile && (
                                                                    <Chip label="Nascosto" size="small" color="warning" variant="outlined" />
                                                                )}
                                                            </Box>
                                                        </Box>
                                                    ))}
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    );
                                })()}

                                {/* Sezione Sostituzioni - Squadra Casa */}
                                {(() => {
                                    const sostituzioniCasa = sostituzioniPartita.filter(s => s.teamId === partita.teamCasaId);
                                    return sostituzioniCasa.length > 0 && (
                                        <Grid item xs={12} md={6}>
                                            <Card sx={{ borderRadius: 2, border: '2px solid #1976d2' }}>
                                                <CardContent>
                                                    <Typography variant="h6" gutterBottom sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                        color: '#1976d2',
                                                        fontWeight: 600
                                                    }}>
                                                        <SwapHorizIcon sx={{ fontSize: 24 }} />
                                                        Sostituzioni {partita.teamCasaNome} ({sostituzioniCasa.length})
                                                    </Typography>

                                                    {sostituzioniCasa.map((sost, index) => (
                                                        <Box key={sost.id} sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 2,
                                                            p: 2,
                                                            mb: 1,
                                                            bgcolor: index % 2 === 0 ? '#e3f2fd' : 'transparent',
                                                            borderRadius: 1,
                                                            border: '1px solid #bbdefb'
                                                        }}>
                                                            <Box sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 1,
                                                                minWidth: 80
                                                            }}>
                                                                <AccessTimeIcon sx={{ fontSize: 16, color: '#666' }} />
                                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                    {sost.minuto}'
                                                                </Typography>
                                                            </Box>

                                                            <SwapHorizIcon sx={{ fontSize: 20, color: '#1976d2' }} />

                                                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flex: 1 }}>
                                                                {/* Giocatore Entrante */}
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                                                                    <Tooltip title={sost.usersNome ? `Inserito da: ${sost.usersNome}${sost.usersEmail ? ` (${sost.usersEmail})` : ''}` : 'Utente non disponibile'} arrow>
                                                                        <Avatar src={sost.playerEntranteFoto} sx={{ width: 40, height: 40, border: '2px solid #4caf50' }}>
                                                                            {sost.playerEntranteNome?.charAt(0)}
                                                                        </Avatar>
                                                                    </Tooltip>
                                                                    <Box>
                                                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                                                                            ⬆️ {sost.playerEntranteNome}
                                                                        </Typography>
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            Entra
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>

                                                                {/* Giocatore Uscente */}
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                                                                    <Tooltip title={sost.usersNome ? `Inserito da: ${sost.usersNome}${sost.usersEmail ? ` (${sost.usersEmail})` : ''}` : 'Utente non disponibile'} arrow>
                                                                        <Avatar src={sost.playerUscenteFoto} sx={{ width: 40, height: 40, border: '2px solid #f44336' }}>
                                                                            {sost.playerUscenteNome?.charAt(0)}
                                                                        </Avatar>
                                                                    </Tooltip>
                                                                    <Box>
                                                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#d32f2f' }}>
                                                                            ⬇️ {sost.playerUscenteNome}
                                                                        </Typography>
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            Esce
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                            </Box>

                                                            {sost.note && (
                                                                <Box sx={{ maxWidth: 150 }}>
                                                                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                                        💬 {sost.note}
                                                                    </Typography>
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    ))}
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    );
                                })()}

                                {/* Sezione Sostituzioni - Squadra Ospite */}
                                {(() => {
                                    const sostituzioniOspite = sostituzioniPartita.filter(s => s.teamId === partita.teamOspiteId);
                                    return sostituzioniOspite.length > 0 && (
                                        <Grid item xs={12} md={6}>
                                            <Card sx={{ borderRadius: 2, border: '2px solid #d32f2f' }}>
                                                <CardContent>
                                                    <Typography variant="h6" gutterBottom sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                        color: '#d32f2f',
                                                        fontWeight: 600
                                                    }}>
                                                        <SwapHorizIcon sx={{ fontSize: 24 }} />
                                                        Sostituzioni {partita.teamOspiteNome} ({sostituzioniOspite.length})
                                                    </Typography>

                                                    {sostituzioniOspite.map((sost, index) => (
                                                        <Box key={sost.id} sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 2,
                                                            p: 2,
                                                            mb: 1,
                                                            bgcolor: index % 2 === 0 ? '#ffebee' : 'transparent',
                                                            borderRadius: 1,
                                                            border: '1px solid #ef9a9a'
                                                        }}>
                                                            <Box sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 1,
                                                                minWidth: 80
                                                            }}>
                                                                <AccessTimeIcon sx={{ fontSize: 16, color: '#666' }} />
                                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                    {sost.minuto}'
                                                                </Typography>
                                                            </Box>

                                                            <SwapHorizIcon sx={{ fontSize: 20, color: '#d32f2f' }} />

                                                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flex: 1 }}>
                                                                {/* Giocatore Entrante */}
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                                                                    <Tooltip title={sost.usersNome ? `Inserito da: ${sost.usersNome}${sost.usersEmail ? ` (${sost.usersEmail})` : ''}` : 'Utente non disponibile'} arrow>
                                                                        <Avatar src={sost.playerEntranteFoto} sx={{ width: 40, height: 40, border: '2px solid #4caf50' }}>
                                                                            {sost.playerEntranteNome?.charAt(0)}
                                                                        </Avatar>
                                                                    </Tooltip>
                                                                    <Box>
                                                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                                                                            ⬆️ {sost.playerEntranteNome}
                                                                        </Typography>
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            Entra
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>

                                                                {/* Giocatore Uscente */}
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                                                                    <Tooltip title={sost.usersNome ? `Inserito da: ${sost.usersNome}${sost.usersEmail ? ` (${sost.usersEmail})` : ''}` : 'Utente non disponibile'} arrow>
                                                                        <Avatar src={sost.playerUscenteFoto} sx={{ width: 40, height: 40, border: '2px solid #f44336' }}>
                                                                            {sost.playerUscenteNome?.charAt(0)}
                                                                        </Avatar>
                                                                    </Tooltip>
                                                                    <Box>
                                                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#d32f2f' }}>
                                                                            ⬇️ {sost.playerUscenteNome}
                                                                        </Typography>
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            Esce
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                            </Box>

                                                            {sost.note && (
                                                                <Box sx={{ maxWidth: 150 }}>
                                                                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                                        💬 {sost.note}
                                                                    </Typography>
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    ))}
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    );
                                })()}

                                {/* Timeline cronologica */}
                                {(goalPartita.length > 0 || cartellinoPartita.length > 0 || sostituzioniPartita.length > 0) && (
                                    <Grid item xs={12}>
                                        <Paper sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                                            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                                                ⏱️ Timeline Cronologica
                                            </Typography>
                                            {[
                                                ...goalPartita.map(g => ({ ...g, tipo: 'goal' })),
                                                ...cartellinoPartita.map(c => ({ ...c, tipo: 'cartellino' })),
                                                ...sostituzioniPartita.map(s => ({ ...s, tipo: 'sostituzione' }))
                                            ]
                                                .sort((a, b) => (a.minuto || 0) - (b.minuto || 0))
                                                .map((evento, index) => {
                                                    const isAutogoal = evento.tipo === 'goal' && (evento.autogoal === 1 || evento.autogoal === true);
                                                    const borderColor = isAutogoal ? '#d32f2f' :
                                                        (evento.tipo === 'goal' ? '#2e7d32' :
                                                            (evento.tipo === 'sostituzione' ? '#1976d2' : '#ff9800'));
                                                    const textColor = isAutogoal ? '#d32f2f' :
                                                        (evento.tipo === 'goal' ? '#2e7d32' :
                                                            (evento.tipo === 'sostituzione' ? '#1976d2' : '#ff9800'));

                                                    return (
                                                        <Box key={`${evento.tipo}-${evento.id}`} sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 2,
                                                            p: 1,
                                                            mb: 1,
                                                            borderLeft: `3px solid ${borderColor}`,
                                                            pl: 2,
                                                            bgcolor: isAutogoal ? '#ffebee' : 'transparent'
                                                        }}>
                                                            <Typography variant="body2" sx={{
                                                                fontWeight: 600,
                                                                minWidth: 40,
                                                                color: textColor
                                                            }}>
                                                                {evento.minuto}'
                                                            </Typography>

                                                            {evento.tipo === 'goal' ? (
                                                                <SportsSoccerIcon sx={{ fontSize: 16, color: isAutogoal ? '#d32f2f' : '#2e7d32' }} />
                                                            ) : evento.tipo === 'sostituzione' ? (
                                                                <SwapHorizIcon sx={{ fontSize: 16, color: '#1976d2' }} />
                                                            ) : (
                                                                evento.tipoCartellino?.includes('giallo') ? (
                                                                    <CropSquareIcon sx={{ fontSize: 16, color: '#ffc107' }} />
                                                                ) : (
                                                                    <StopIcon sx={{ fontSize: 16, color: '#d32f2f' }} />
                                                                )
                                                            )}

                                                            <Typography variant="body2">
                                                                {evento.tipo === 'goal' ? (
                                                                    <>
                                                                        <strong>{evento.playerName}</strong> -
                                                                        {isAutogoal ? '🔴 Autogoal' : 'Goal'}
                                                                        {evento.playerTeam && ` (${evento.playerTeam})`}
                                                                    </>
                                                                ) : evento.tipo === 'sostituzione' ? (
                                                                    <>
                                                                        🔄 <strong>{evento.playerEntranteNome}</strong> ↔️ <strong>{evento.playerUscenteNome}</strong>
                                                                        {evento.playerEntranteTeam && ` (${evento.playerEntranteTeam})`}
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <strong>{evento.playerName}</strong> -
                                                                        {evento.tipoCartellinoNome || 'Cartellino'}
                                                                        {evento.playerTeam && ` (${evento.playerTeam})`}
                                                                    </>
                                                                )}
                                                            </Typography>
                                                        </Box>
                                                    );
                                                })
                                            }
                                        </Paper>
                                    </Grid>
                                )}
                            </Grid>
                        ) : (
                            <Alert severity="info" sx={{ mt: 2 }}>
                                <Typography variant="body2">
                                    ⚡ <strong>Nessun evento registrato</strong><br />
                                    Non ci sono ancora goal, cartellini o sostituzioni per questa partita.
                                </Typography>
                            </Alert>
                        )}
                    </TabPanel>

                    <TabPanel value={tabValue} index={3}>
                        {/* Tab MVP */}
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <EmojiEventsIcon sx={{ fontSize: 24, color: '#ffd700' }} />
                            MVP della Partita
                        </Typography>

                        {loadingMvp ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                <CircularProgress />
                            </Box>
                        ) : mvpPartita.length > 0 ? (
                            <Grid container spacing={3}>
                                {/* MVP Squadra Casa */}
                                <Grid item xs={12} md={6}>
                                    <Typography variant="h6" sx={{
                                        mb: 2,
                                        color: '#1976d2',
                                        fontWeight: 700,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1
                                    }}>
                                        🏠 {partita?.nomeCasa || 'Squadra Casa'}
                                    </Typography>
                                    {mvpPartita.filter(mvp => mvp.playerTeamId === partita?.teamCasaId).map((mvp, index) => (
                                        <Card key={mvp.id} sx={{
                                            mb: 2,
                                            borderRadius: 3,
                                            border: '3px solid #ffd700',
                                            boxShadow: '0 4px 12px rgba(255, 215, 0, 0.3)',
                                            background: 'linear-gradient(135deg, #fffbf0 0%, #fff8dc 100%)'
                                        }}>
                                            <CardContent sx={{ p: 3 }}>
                                                <Box sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 3,
                                                    mb: 2
                                                }}>
                                                    {/* Icona MVP */}
                                                    <Box sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        width: 60,
                                                        height: 60,
                                                        borderRadius: '50%',
                                                        background: 'linear-gradient(135deg, #ffd700 0%, #ffed4e 100%)',
                                                        boxShadow: '0 4px 8px rgba(255, 215, 0, 0.4)'
                                                    }}>
                                                        <EmojiEventsIcon sx={{ fontSize: 32, color: '#b8860b' }} />
                                                    </Box>

                                                    {/* Avatar e info giocatore */}
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                                                        <Avatar
                                                            src={mvp.playerFoto}
                                                            sx={{
                                                                width: 80,
                                                                height: 80,
                                                                border: '3px solid #ffd700',
                                                                boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                                                            }}
                                                        >
                                                            {mvp.playerName?.charAt(0)?.toUpperCase() || '?'}
                                                        </Avatar>

                                                        <Box sx={{ flex: 1 }}>
                                                            <Typography variant="h4" sx={{
                                                                fontWeight: 700,
                                                                color: '#b8860b',
                                                                mb: 0.5,
                                                                textShadow: '1px 1px 2px rgba(0,0,0,0.1)'
                                                            }}>
                                                                🏆 {mvp.playerName || `Giocatore ${mvp.playerId}`}
                                                            </Typography>

                                                            {mvp.playerTeam && (
                                                                <Typography variant="h6" sx={{
                                                                    color: '#8b7355',
                                                                    fontWeight: 500,
                                                                    mb: 1
                                                                }}>
                                                                    👥 {mvp.playerTeam}
                                                                </Typography>
                                                            )}

                                                            {mvp.note && (
                                                                <Typography variant="body1" sx={{
                                                                    color: '#6d5a47',
                                                                    fontStyle: 'italic',
                                                                    bgcolor: 'rgba(255, 215, 0, 0.1)',
                                                                    p: 1,
                                                                    borderRadius: 1,
                                                                    border: '1px dashed #ffd700'
                                                                }}>
                                                                    💬 "{mvp.note}"
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </Box>

                                                    {/* Badge MVP */}
                                                    <Box sx={{ textAlign: 'center' }}>
                                                        <Chip
                                                            label="MVP"
                                                            sx={{
                                                                bgcolor: '#ffd700',
                                                                color: '#b8860b',
                                                                fontWeight: 700,
                                                                fontSize: '1.1rem',
                                                                height: 40,
                                                                px: 2,
                                                                border: '2px solid #b8860b',
                                                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                                            }}
                                                        />

                                                        <Typography variant="caption" sx={{
                                                            display: 'block',
                                                            mt: 1,
                                                            color: '#8b7355',
                                                            fontWeight: 600
                                                        }}>
                                                            Player of the Match
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                {/* Immagine MVP se disponibile */}
                                                {mvp.mvpImage && (
                                                    <Box sx={{ mt: 3, textAlign: 'center' }}>
                                                        <Typography variant="subtitle2" sx={{
                                                            color: '#8b7355',
                                                            fontWeight: 600,
                                                            mb: 1,
                                                            textTransform: 'uppercase',
                                                            letterSpacing: 1
                                                        }}>
                                                            📸 Foto MVP
                                                        </Typography>
                                                        <Box sx={{
                                                            display: 'inline-block',
                                                            borderRadius: 2,
                                                            overflow: 'hidden',
                                                            border: '3px solid #ffd700',
                                                            boxShadow: '0 4px 12px rgba(255, 215, 0, 0.4)',
                                                            maxWidth: '100%',
                                                            cursor: 'pointer',
                                                            transition: 'transform 0.2s',
                                                            '&:hover': {
                                                                transform: 'scale(1.02)'
                                                            }
                                                        }}
                                                            onClick={() => handleOpenImageDialog(mvp.mvpImage, mvp.playerName)}
                                                        >
                                                            <img
                                                                src={mvp.mvpImage}
                                                                alt={`Foto MVP ${mvp.playerName}`}
                                                                style={{
                                                                    maxWidth: '400px',
                                                                    maxHeight: '300px',
                                                                    width: 'auto',
                                                                    height: 'auto',
                                                                    display: 'block'
                                                                }}
                                                            />
                                                        </Box>
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={() => handleOpenImageDialog(mvp.mvpImage, mvp.playerName)}
                                                            sx={{
                                                                mt: 2,
                                                                color: '#b8860b',
                                                                borderColor: '#ffd700',
                                                                '&:hover': {
                                                                    borderColor: '#b8860b',
                                                                    bgcolor: 'rgba(255, 215, 0, 0.1)'
                                                                }
                                                            }}
                                                        >
                                                            🔍 Visualizza Ingrandita
                                                        </Button>
                                                    </Box>
                                                )}

                                                {/* Stati e info aggiuntive */}
                                                <Box sx={{ pt: 2, borderTop: '1px solid rgba(255, 215, 0, 0.3)' }}>
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                                        {mvp.flAnnullato ? (
                                                            <Chip
                                                                label="Annullato"
                                                                size="small"
                                                                color="error"
                                                                variant="outlined"
                                                                sx={{ fontWeight: 600 }}
                                                            />
                                                        ) : (
                                                            <Chip
                                                                label="Valido"
                                                                size="small"
                                                                color="success"
                                                                variant="outlined"
                                                                sx={{ fontWeight: 600 }}
                                                            />
                                                        )}

                                                        {!mvp.flVisibile && (
                                                            <Chip
                                                                label="Nascosto"
                                                                size="small"
                                                                color="warning"
                                                                variant="outlined"
                                                                sx={{ fontWeight: 600 }}
                                                            />
                                                        )}

                                                        {!mvp.flAttivo && (
                                                            <Chip
                                                                label="Inattivo"
                                                                size="small"
                                                                color="default"
                                                                variant="outlined"
                                                                sx={{ fontWeight: 600 }}
                                                            />
                                                        )}
                                                    </Box>

                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                                                        <Chip
                                                            label={`Post ID: ${mvp.postId || 'N/A'}`}
                                                            size="small"
                                                            sx={{
                                                                bgcolor: '#e3f2fd',
                                                                color: '#1976d2',
                                                                fontWeight: 600,
                                                                fontFamily: 'monospace'
                                                            }}
                                                        />
                                                        <Chip
                                                            label={`User ID: ${mvp.usersId || 'N/A'}`}
                                                            size="small"
                                                            sx={{
                                                                bgcolor: '#f3e5f5',
                                                                color: '#9c27b0',
                                                                fontWeight: 600,
                                                                fontFamily: 'monospace'
                                                            }}
                                                        />
                                                        <Chip
                                                            label={`Team ID: ${mvp.playerTeamId || 'N/A'}`}
                                                            size="small"
                                                            sx={{
                                                                bgcolor: '#fff3e0',
                                                                color: '#f57c00',
                                                                fontWeight: 600,
                                                                fontFamily: 'monospace'
                                                            }}
                                                        />
                                                    </Box>

                                                    <Typography variant="caption" color="text.secondary">
                                                        📅 Assegnato il {new Date(mvp.createdAt).toLocaleDateString('it-IT')}
                                                        {mvp.updatedAt && mvp.updatedAt !== mvp.createdAt && (
                                                            <span> • Aggiornato il {new Date(mvp.updatedAt).toLocaleDateString('it-IT')}</span>
                                                        )}
                                                    </Typography>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    ))}

                                    {mvpPartita.filter(mvp => mvp.playerTeamId === partita?.teamCasaId).length === 0 && (
                                        <Alert severity="info" sx={{ mt: 2 }}>
                                            Nessun MVP assegnato per questa squadra
                                        </Alert>
                                    )}
                                </Grid>

                                {/* MVP Squadra Ospite */}
                                <Grid item xs={12} md={6}>
                                    <Typography variant="h6" sx={{
                                        mb: 2,
                                        color: '#d32f2f',
                                        fontWeight: 700,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1
                                    }}>
                                        ✈️ {partita?.nomeOspite || 'Squadra Ospite'}
                                    </Typography>
                                    {mvpPartita.filter(mvp => mvp.playerTeamId === partita?.teamOspiteId).map((mvp, index) => (
                                        <Card key={mvp.id} sx={{
                                            mb: 2,
                                            borderRadius: 3,
                                            border: '3px solid #ffd700',
                                            boxShadow: '0 4px 12px rgba(255, 215, 0, 0.3)',
                                            background: 'linear-gradient(135deg, #fffbf0 0%, #fff8dc 100%)'
                                        }}>
                                            <CardContent sx={{ p: 3 }}>
                                                <Box sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 3,
                                                    mb: 2
                                                }}>
                                                    {/* Icona MVP */}
                                                    <Box sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        width: 60,
                                                        height: 60,
                                                        borderRadius: '50%',
                                                        background: 'linear-gradient(135deg, #ffd700 0%, #ffed4e 100%)',
                                                        boxShadow: '0 4px 8px rgba(255, 215, 0, 0.4)'
                                                    }}>
                                                        <EmojiEventsIcon sx={{ fontSize: 32, color: '#b8860b' }} />
                                                    </Box>

                                                    {/* Avatar e info giocatore */}
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                                                        <Avatar
                                                            src={mvp.playerFoto}
                                                            sx={{
                                                                width: 80,
                                                                height: 80,
                                                                border: '3px solid #ffd700',
                                                                boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                                                            }}
                                                        >
                                                            {mvp.playerName?.charAt(0)?.toUpperCase() || '?'}
                                                        </Avatar>

                                                        <Box sx={{ flex: 1 }}>
                                                            <Typography variant="h4" sx={{
                                                                fontWeight: 700,
                                                                color: '#b8860b',
                                                                mb: 0.5,
                                                                textShadow: '1px 1px 2px rgba(0,0,0,0.1)'
                                                            }}>
                                                                🏆 {mvp.playerName || `Giocatore ${mvp.playerId}`}
                                                            </Typography>

                                                            {mvp.playerTeam && (
                                                                <Typography variant="h6" sx={{
                                                                    color: '#8b7355',
                                                                    fontWeight: 500,
                                                                    mb: 1
                                                                }}>
                                                                    👥 {mvp.playerTeam}
                                                                </Typography>
                                                            )}

                                                            {mvp.note && (
                                                                <Typography variant="body1" sx={{
                                                                    color: '#6d5a47',
                                                                    fontStyle: 'italic',
                                                                    bgcolor: 'rgba(255, 215, 0, 0.1)',
                                                                    p: 1,
                                                                    borderRadius: 1,
                                                                    border: '1px dashed #ffd700'
                                                                }}>
                                                                    💬 "{mvp.note}"
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </Box>

                                                    {/* Badge MVP */}
                                                    <Box sx={{ textAlign: 'center' }}>
                                                        <Chip
                                                            label="MVP"
                                                            sx={{
                                                                bgcolor: '#ffd700',
                                                                color: '#b8860b',
                                                                fontWeight: 700,
                                                                fontSize: '1.1rem',
                                                                height: 40,
                                                                px: 2,
                                                                border: '2px solid #b8860b',
                                                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                                            }}
                                                        />

                                                        <Typography variant="caption" sx={{
                                                            display: 'block',
                                                            mt: 1,
                                                            color: '#8b7355',
                                                            fontWeight: 600
                                                        }}>
                                                            Player of the Match
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                {/* Immagine MVP se disponibile */}
                                                {mvp.mvpImage && (
                                                    <Box sx={{ mt: 3, textAlign: 'center' }}>
                                                        <Typography variant="subtitle2" sx={{
                                                            color: '#8b7355',
                                                            fontWeight: 600,
                                                            mb: 1,
                                                            textTransform: 'uppercase',
                                                            letterSpacing: 1
                                                        }}>
                                                            📸 Foto MVP
                                                        </Typography>
                                                        <Box sx={{
                                                            display: 'inline-block',
                                                            borderRadius: 2,
                                                            overflow: 'hidden',
                                                            border: '3px solid #ffd700',
                                                            boxShadow: '0 4px 12px rgba(255, 215, 0, 0.4)',
                                                            maxWidth: '100%',
                                                            cursor: 'pointer',
                                                            transition: 'transform 0.2s',
                                                            '&:hover': {
                                                                transform: 'scale(1.02)'
                                                            }
                                                        }}
                                                            onClick={() => handleOpenImageDialog(mvp.mvpImage, mvp.playerName)}
                                                        >
                                                            <img
                                                                src={mvp.mvpImage}
                                                                alt={`Foto MVP ${mvp.playerName}`}
                                                                style={{
                                                                    maxWidth: '400px',
                                                                    maxHeight: '300px',
                                                                    width: 'auto',
                                                                    height: 'auto',
                                                                    display: 'block'
                                                                }}
                                                            />
                                                        </Box>
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={() => handleOpenImageDialog(mvp.mvpImage, mvp.playerName)}
                                                            sx={{
                                                                mt: 2,
                                                                color: '#b8860b',
                                                                borderColor: '#ffd700',
                                                                '&:hover': {
                                                                    borderColor: '#b8860b',
                                                                    bgcolor: 'rgba(255, 215, 0, 0.1)'
                                                                }
                                                            }}
                                                        >
                                                            🔍 Visualizza Ingrandita
                                                        </Button>
                                                    </Box>
                                                )}

                                                {/* Stati e info aggiuntive */}
                                                <Box sx={{ pt: 2, borderTop: '1px solid rgba(255, 215, 0, 0.3)' }}>
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                                        {mvp.flAnnullato ? (
                                                            <Chip
                                                                label="Annullato"
                                                                size="small"
                                                                color="error"
                                                                variant="outlined"
                                                                sx={{ fontWeight: 600 }}
                                                            />
                                                        ) : (
                                                            <Chip
                                                                label="Valido"
                                                                size="small"
                                                                color="success"
                                                                variant="outlined"
                                                                sx={{ fontWeight: 600 }}
                                                            />
                                                        )}

                                                        {!mvp.flVisibile && (
                                                            <Chip
                                                                label="Nascosto"
                                                                size="small"
                                                                color="warning"
                                                                variant="outlined"
                                                                sx={{ fontWeight: 600 }}
                                                            />
                                                        )}

                                                        {!mvp.flAttivo && (
                                                            <Chip
                                                                label="Inattivo"
                                                                size="small"
                                                                color="default"
                                                                variant="outlined"
                                                                sx={{ fontWeight: 600 }}
                                                            />
                                                        )}
                                                    </Box>

                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                                                        <Chip
                                                            label={`Post ID: ${mvp.postId || 'N/A'}`}
                                                            size="small"
                                                            sx={{
                                                                bgcolor: '#e3f2fd',
                                                                color: '#1976d2',
                                                                fontWeight: 600,
                                                                fontFamily: 'monospace'
                                                            }}
                                                        />
                                                        <Chip
                                                            label={`User ID: ${mvp.usersId || 'N/A'}`}
                                                            size="small"
                                                            sx={{
                                                                bgcolor: '#f3e5f5',
                                                                color: '#9c27b0',
                                                                fontWeight: 600,
                                                                fontFamily: 'monospace'
                                                            }}
                                                        />
                                                        <Chip
                                                            label={`Team ID: ${mvp.playerTeamId || 'N/A'}`}
                                                            size="small"
                                                            sx={{
                                                                bgcolor: '#fff3e0',
                                                                color: '#f57c00',
                                                                fontWeight: 600,
                                                                fontFamily: 'monospace'
                                                            }}
                                                        />
                                                    </Box>

                                                    <Typography variant="caption" color="text.secondary">
                                                        📅 Assegnato il {new Date(mvp.createdAt).toLocaleDateString('it-IT')}
                                                        {mvp.updatedAt && mvp.updatedAt !== mvp.createdAt && (
                                                            <span> • Aggiornato il {new Date(mvp.updatedAt).toLocaleDateString('it-IT')}</span>
                                                        )}
                                                    </Typography>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    ))}

                                    {mvpPartita.filter(mvp => mvp.playerTeamId === partita?.teamOspiteId).length === 0 && (
                                        <Alert severity="info" sx={{ mt: 2 }}>
                                            Nessun MVP assegnato per questa squadra
                                        </Alert>
                                    )}
                                </Grid>

                                {/* Statistiche MVP */}
                                <Grid item xs={12}>
                                    <Paper sx={{
                                        p: 3,
                                        bgcolor: '#f8f9fa',
                                        borderRadius: 2,
                                        border: '1px solid #e0e0e0'
                                    }}>
                                        <Typography variant="subtitle1" gutterBottom sx={{
                                            fontWeight: 600,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1
                                        }}>
                                            📊 Statistiche MVP
                                        </Typography>

                                        <Grid container spacing={2}>
                                            <Grid item xs={6} md={3}>
                                                <Box sx={{ textAlign: 'center' }}>
                                                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#ffd700' }}>
                                                        {mvpPartita.length}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        MVP Totali
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={6} md={3}>
                                                <Box sx={{ textAlign: 'center' }}>
                                                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#2e7d32' }}>
                                                        {mvpPartita.filter(m => !m.flAnnullato).length}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        MVP Validi
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={6} md={3}>
                                                <Box sx={{ textAlign: 'center' }}>
                                                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#1976d2' }}>
                                                        {mvpPartita.filter(m => m.flVisibile).length}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        MVP Visibili
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={6} md={3}>
                                                <Box sx={{ textAlign: 'center' }}>
                                                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#ff9800' }}>
                                                        {[...new Set(mvpPartita.map(m => m.playerTeam))].filter(Boolean).length}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Squadre Diverse
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        </Grid>

                                        {mvpPartita.some(m => m.note) && (
                                            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                                                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                                                    📝 Note MVP:
                                                </Typography>
                                                {mvpPartita.filter(m => m.note).map(mvp => (
                                                    <Typography key={mvp.id} variant="body2" sx={{
                                                        mb: 1,
                                                        pl: 2,
                                                        borderLeft: '3px solid #ffd700',
                                                        fontStyle: 'italic'
                                                    }}>
                                                        <strong>{mvp.playerName}:</strong> "{mvp.note}"
                                                    </Typography>
                                                ))}
                                            </Box>
                                        )}
                                    </Paper>
                                </Grid>
                            </Grid>
                        ) : (
                            <Alert severity="info" sx={{ mt: 2 }}>
                                <Typography variant="body2">
                                    🏆 <strong>Nessun MVP assegnato</strong><br />
                                    Non è ancora stato assegnato un MVP per questa partita.
                                </Typography>
                            </Alert>
                        )}
                    </TabPanel>

                    <TabPanel value={tabValue} index={4}>
                        {/* Tab Risultati */}
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <SportsSoccerIcon sx={{ fontSize: 24, color: '#1976d2' }} />
                            ⚽ Risultati Partita
                        </Typography>

                        {loadingRisultati ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <>
                                {/* Risultato Ufficiale */}
                                {(() => {
                                    const risultatoUfficiale = risultatiPartita.find(r => r.risultatoUfficiale === 1);
                                    const altriRisultati = risultatiPartita.filter(r => r.risultatoUfficiale === 0);

                                    return (
                                        <>
                                            {risultatoUfficiale ? (
                                                <Card sx={{ mb: 3, bgcolor: '#e8f5e8', border: '2px solid #4caf50' }}>
                                                    <CardContent>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                                            <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 24 }} />
                                                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#2e7d32' }}>
                                                                🏆 RISULTATO UFFICIALE
                                                            </Typography>
                                                        </Box>

                                                        <Box sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            p: 3,
                                                            bgcolor: 'white',
                                                            borderRadius: 2
                                                        }}>
                                                            <Typography variant="h2" sx={{ fontWeight: 700, color: '#1976d2' }}>
                                                                {risultatoUfficiale.goalCasa} - {risultatoUfficiale.goalOspite}
                                                            </Typography>
                                                        </Box>

                                                        {risultatoUfficiale.risultatoNote && (
                                                            <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(255,255,255,0.7)', borderRadius: 1 }}>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    💬 {risultatoUfficiale.risultatoNote}
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                    </CardContent>
                                                </Card>
                                            ) : (
                                                <Alert severity="warning" sx={{ mb: 3 }}>
                                                    ⚠️ Nessun risultato ufficiale presente per questa partita
                                                </Alert>
                                            )}

                                            {/* Form Aggiungi Risultato */}
                                            <Card sx={{ mb: 3, bgcolor: '#f5f5f5' }}>
                                                <CardContent>
                                                    {!showAddResultForm ? (
                                                        <Button
                                                            variant="contained"
                                                            color="primary"
                                                            startIcon={<AddIcon />}
                                                            onClick={() => setShowAddResultForm(true)}
                                                            fullWidth
                                                            size="large"
                                                        >
                                                            ➕ Aggiungi Nuovo Risultato
                                                        </Button>
                                                    ) : (
                                                        <Box>
                                                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <SportsSoccerIcon color="primary" />
                                                                Inserisci Nuovo Risultato
                                                            </Typography>

                                                            <Grid container spacing={2}>
                                                                <Grid item xs={12} md={6}>
                                                                    <TextField
                                                                        fullWidth
                                                                        type="number"
                                                                        label={`⚽ Goal ${partita?.teamCasaNome || 'Casa'}`}
                                                                        value={newResultForm.goalCasa}
                                                                        onChange={(e) => setNewResultForm({ ...newResultForm, goalCasa: Math.max(0, parseInt(e.target.value) || 0) })}
                                                                        inputProps={{ min: 0, step: 1 }}
                                                                        disabled={creatingResult}
                                                                    />
                                                                </Grid>

                                                                <Grid item xs={12} md={6}>
                                                                    <TextField
                                                                        fullWidth
                                                                        type="number"
                                                                        label={`⚽ Goal ${partita?.teamOspiteNome || 'Ospite'}`}
                                                                        value={newResultForm.goalOspite}
                                                                        onChange={(e) => setNewResultForm({ ...newResultForm, goalOspite: Math.max(0, parseInt(e.target.value) || 0) })}
                                                                        inputProps={{ min: 0, step: 1 }}
                                                                        disabled={creatingResult}
                                                                    />
                                                                </Grid>

                                                                <Grid item xs={12}>
                                                                    <Box sx={{
                                                                        p: 2,
                                                                        bgcolor: '#e3f2fd',
                                                                        borderRadius: 2,
                                                                        textAlign: 'center',
                                                                        border: '2px solid #1976d2'
                                                                    }}>
                                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                                                            Anteprima Risultato
                                                                        </Typography>
                                                                        <Typography variant="h3" sx={{ fontWeight: 700, color: '#1976d2' }}>
                                                                            {newResultForm.goalCasa} - {newResultForm.goalOspite}
                                                                        </Typography>
                                                                    </Box>
                                                                </Grid>

                                                                <Grid item xs={12}>
                                                                    <TextField
                                                                        fullWidth
                                                                        multiline
                                                                        rows={2}
                                                                        label="📝 Note (opzionale)"
                                                                        value={newResultForm.note}
                                                                        onChange={(e) => setNewResultForm({ ...newResultForm, note: e.target.value })}
                                                                        placeholder="Aggiungi note o commenti sul risultato..."
                                                                        disabled={creatingResult}
                                                                        inputProps={{ maxLength: 1000 }}
                                                                        helperText={`${newResultForm.note.length}/1000 caratteri`}
                                                                    />
                                                                </Grid>

                                                                <Grid item xs={12}>
                                                                    <FormControlLabel
                                                                        control={
                                                                            <Checkbox
                                                                                checked={newResultForm.flUfficiale === 1}
                                                                                onChange={(e) => setNewResultForm({ ...newResultForm, flUfficiale: e.target.checked ? 1 : 0 })}
                                                                                disabled={creatingResult}
                                                                                color="success"
                                                                            />
                                                                        }
                                                                        label={
                                                                            <Box>
                                                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                                    🏆 Imposta come Risultato Ufficiale
                                                                                </Typography>
                                                                                <Typography variant="caption" color="text.secondary">
                                                                                    Se selezionato, questo risultato diventerà immediatamente ufficiale
                                                                                </Typography>
                                                                            </Box>
                                                                        }
                                                                    />
                                                                </Grid>

                                                                <Grid item xs={12}>
                                                                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                                                                        <Button
                                                                            variant="outlined"
                                                                            onClick={() => {
                                                                                setShowAddResultForm(false);
                                                                                setNewResultForm({
                                                                                    goalCasa: 0,
                                                                                    goalOspite: 0,
                                                                                    note: '',
                                                                                    flUfficiale: 0
                                                                                });
                                                                            }}
                                                                            disabled={creatingResult}
                                                                        >
                                                                            Annulla
                                                                        </Button>
                                                                        <Button
                                                                            variant="contained"
                                                                            color="success"
                                                                            startIcon={creatingResult ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
                                                                            onClick={handleCreaRisultato}
                                                                            disabled={creatingResult}
                                                                        >
                                                                            {creatingResult ? 'Creazione...' : 'Crea Risultato'}
                                                                        </Button>
                                                                    </Box>
                                                                </Grid>
                                                            </Grid>
                                                        </Box>
                                                    )}
                                                </CardContent>
                                            </Card>

                                            {/* Altri Risultati */}
                                            {altriRisultati.length > 0 && (
                                                <Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                                        <PendingIcon sx={{ color: '#ff9800' }} />
                                                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#e65100' }}>
                                                            📋 Altri Risultati Registrati ({altriRisultati.length})
                                                        </Typography>
                                                    </Box>

                                                    <Grid container spacing={2}>
                                                        {altriRisultati.map((risultato) => (
                                                            <Grid item xs={12} key={risultato.Id}>
                                                                <Card sx={{
                                                                    bgcolor: '#f8f9fa',
                                                                    border: '1px solid #e0e0e0',
                                                                    '&:hover': {
                                                                        bgcolor: '#f0f0f0',
                                                                        borderColor: '#1976d2'
                                                                    }
                                                                }}>
                                                                    <CardContent>
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                                                <Typography variant="h5" sx={{
                                                                                    fontWeight: 700,
                                                                                    color: '#666',
                                                                                    minWidth: 80,
                                                                                    textAlign: 'center',
                                                                                    bgcolor: 'white',
                                                                                    borderRadius: 1,
                                                                                    p: 1
                                                                                }}>
                                                                                    {risultato.goalCasa} - {risultato.goalOspite}
                                                                                </Typography>

                                                                                <Box>
                                                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                                        Risultato N°{risultato.Id}
                                                                                    </Typography>
                                                                                    {risultato.risultatoNote && (
                                                                                        <Typography variant="caption" color="text.secondary">
                                                                                            💬 {risultato.risultatoNote}
                                                                                        </Typography>
                                                                                    )}
                                                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                                                        📅 {new Date(risultato.createdAt).toLocaleString('it-IT')}
                                                                                    </Typography>
                                                                                </Box>
                                                                            </Box>

                                                                            <Button
                                                                                variant="contained"
                                                                                size="small"
                                                                                color="success"
                                                                                onClick={() => handleRendiUfficiale(risultato.Id)}
                                                                                disabled={loadingRendiUfficiale !== null}
                                                                                startIcon={
                                                                                    loadingRendiUfficiale === risultato.Id ?
                                                                                        <CircularProgress size={16} color="inherit" /> :
                                                                                        <CheckCircleIcon />
                                                                                }
                                                                            >
                                                                                {loadingRendiUfficiale === risultato.Id ?
                                                                                    'Elaborando...' :
                                                                                    'Rendi Ufficiale'
                                                                                }
                                                                            </Button>
                                                                        </Box>
                                                                    </CardContent>
                                                                </Card>
                                                            </Grid>
                                                        ))}
                                                    </Grid>
                                                </Box>
                                            )}
                                        </>
                                    );
                                })()}
                            </>
                        )}
                    </TabPanel>
                </Card>

                {/* Dialog per visualizzare immagine MVP ingrandita */}
                <Dialog
                    open={openImageDialog}
                    onClose={handleCloseImageDialog}
                    maxWidth="lg"
                    fullWidth
                >
                    <DialogContent sx={{
                        p: 0,
                        bgcolor: '#000',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '70vh'
                    }}>
                        {selectedImage.url && (
                            <Box sx={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                p: 2
                            }}>
                                <img
                                    src={selectedImage.url}
                                    alt={`Foto MVP ${selectedImage.name}`}
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '80vh',
                                        width: 'auto',
                                        height: 'auto',
                                        objectFit: 'contain',
                                        borderRadius: '8px',
                                        boxShadow: '0 8px 32px rgba(255, 215, 0, 0.5)'
                                    }}
                                />
                                {selectedImage.name && (
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            mt: 2,
                                            color: '#ffd700',
                                            textAlign: 'center',
                                            fontWeight: 600,
                                            textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
                                        }}
                                    >
                                        🏆 {selectedImage.name}
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ bgcolor: '#1a1a1a', borderTop: '2px solid #ffd700' }}>
                        <Button
                            onClick={handleCloseImageDialog}
                            sx={{
                                color: '#ffd700',
                                fontWeight: 600,
                                '&:hover': {
                                    bgcolor: 'rgba(255, 215, 0, 0.1)'
                                }
                            }}
                        >
                            Chiudi
                        </Button>
                    </DialogActions>
                </Dialog>

            </motion.div>
        </Box>
    );
};

export default PartitaDetailPage;
